import { CollectionViewer } from '@angular/cdk/collections';
import { AgrDataSource } from '@next/core-lib/table-v2';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
  BehaviorSubject,
  Observable,
  Subject,
  combineLatest,
  debounceTime,
  shareReplay,
  takeUntil,
  tap,
  withLatestFrom,
} from 'rxjs';
import { OfferOverviewModel } from 'src/app/dashboard/components/overview/models/overview.model';

import { PageAndSize } from 'src/app/dashboard/components/overview/models/paginated-overview.model';

import * as MyAdvertisementsActions from '../../store/actions/my-advertisements.actions';
import * as MyAdvertisementsSelector from '../../store/selectors/my-advertisements.selector';

export class AdvertisementsClosedDealsOffersDataSource extends AgrDataSource<OfferOverviewModel> {
  public data$ = new BehaviorSubject<OfferOverviewModel[]>([]);
  closedDealsOffers$ = this.store.select(MyAdvertisementsSelector.selectClosedDealsOfferList);

  private pageAndSizeSubject$ = new BehaviorSubject<PageAndSize>({
    pageIndex: 0,
    pageSize: 100,
  });

  private readonly destroySubject$ = new Subject<boolean>();

  constructor(
    private readonly store: Store,
    private readonly actions$: Actions,
  ) {
    super();

    combineLatest([this.pageAndSizeSubject$])
      .pipe(
        debounceTime(100),
        tap(() => this.loadingSubject.next(true)),
        takeUntil(this.destroySubject$),
      )
      .subscribe(([paginationOptions]) => {
        this.store.dispatch(MyAdvertisementsActions.loadClosedOffers({ paginationOptions }));
      });

    this.actions$
      .pipe(
        ofType(MyAdvertisementsActions.deleteOfferSuccess),
        withLatestFrom(this.pageAndSizeSubject$),
        takeUntil(this.destroySubject$),
        tap(([_, pageAndSize]) => {
          this.pageAndSizeSubject$.next(pageAndSize);
        }),
      )
      .subscribe();
  }

  connect(_: CollectionViewer): Observable<OfferOverviewModel[]> {
    combineLatest([this.closedDealsOffers$])
      .pipe(
        debounceTime(100),
        tap(() => this.loadingSubject.next(true)),
        takeUntil(this.destroySubject$),
        shareReplay(1),
      )
      .subscribe(([offers]) => {
        this.loadingSubject.next(false);
        this.data$.next(offers);
      });
    return this.data$;
  }

  disconnect(collectionViewer: CollectionViewer): void {
    super.disconnect(collectionViewer);
    this.destroySubject$.next(true);
    this.destroySubject$.complete();
  }

  applyPage(pageIndex: number, pageSize: number): void {
    this.pageAndSizeSubject$.next({ pageIndex, pageSize });
  }
}
